import React, { useState } from "react";
import MaterialListSelect from "./MaterialListSelect";
import MaterialGroupSelect from "./MaterialGroupSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import axios from "axios";
import { formatNumber } from "../utils/numbers";
import StockThresholdModal from "./StockThresholdModal";

const VIEW_OPTIONS = [
  { value: "0", label: "Show All" },
  { value: "1", label: "Search by Product Code / Name" },
  { value: "2", label: "Search by Group" },
];

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function MaterialCurrentStock() {
  const [viewType, setViewType] = useState("0");
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [productLocation, setProductLocation] = useState("");
  const [showEmpty, setShowEmpty] = useState(true);
  const [currentStock, setCurrentStock] = useState([]);
  const [currentGroupedStock, setCurrentGroupedStock] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [thresholdRecord, setThresholdRecord] = useState(null);

  const formatRecord = (data) => {
    if (!data || data.length === 0) return {};
    return data.reduce((acc, item) => {
      const g = item.group_name;
      if (!acc[g]) acc[g] = [];
      acc[g].push(item);
      return acc;
    }, {});
  };

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    setCurrentStock([]);
    setCurrentGroupedStock({});
    setSelectedMatList(null);
    setSelectedMatGroup(null);
    setSearched(false);
  };

  const getallCurrentStock = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/currentallstock",
      );
      const data = showEmpty
        ? res.data.result
        : res.data.result.filter((i) => i.current_stock > 0);
      setCurrentGroupedStock(formatRecord(data));
      setSearched(true);
    } catch {
      toast.error("Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStockByGroupId = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstockbygroup/${selectedMatGroup}`,
      );
      const data = showEmpty
        ? res.data.result
        : res.data.result.filter((i) => i.current_stock > 0);
      setCurrentStock(data);
      setSearched(true);
    } catch {
      toast.error("Failed to fetch group stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStockById = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstockraw/${selectedMatList}`,
      );
      const data = res.data.result;
      setCurrentStock(
        typeof data === "object" && !Array.isArray(data) ? [data] : data,
      );
      setSearched(true);
    } catch {
      toast.error("Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewType === "1" && !selectedMatList) {
      toast.warning("Please select a product.");
      return;
    }
    if (viewType === "2" && !selectedMatGroup) {
      toast.warning("Please select a group.");
      return;
    }
    if (viewType === "0") getallCurrentStock();
    else if (viewType === "1") getCurrentStockById();
    else if (viewType === "2") getCurrentStockByGroupId();
  };

  const groupEntries = Object.entries(currentGroupedStock);
  const totalItems =
    viewType === "0"
      ? groupEntries.reduce((s, [, items]) => s + items.length, 0)
      : currentStock.length;

  const handleThresholdSaved = (product_id, min_stock) => {
    const patch = (arr) =>
      arr.map((r) => (r.product_id === product_id ? { ...r, min_stock } : r));
    setCurrentStock((prev) => patch(prev));
    setCurrentGroupedStock((prev) => {
      const next = {};
      Object.entries(prev).forEach(([g, items]) => {
        next[g] = patch(items);
      });
      return next;
    });
  };

  const stockBadge = (qty, minStock) => {
    if (qty <= 0) return "bg-red-100 text-red-700 font-bold";
    if (minStock != null && qty <= minStock)
      return "bg-amber-100 text-amber-700 font-bold";
    if (qty < 10) return "bg-yellow-100 text-yellow-700 font-semibold";
    return "text-gray-800 font-medium";
  };

  const MinBadge = ({ data }) =>
    data.min_stock != null ? (
      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
        min: {formatNumber(data.min_stock)}
      </span>
    ) : (
      <span className="text-xs text-gray-300 italic">—</span>
    );

  /* ── Shared table rows ──────────────────────────────────────────── */
  const renderRows = () => {
    if (viewType === "0") {
      return groupEntries.map(([groupName, groupItems], gi) => (
        <React.Fragment key={`g-${gi}`}>
          <tr className="bg-cyan-700 border-t-4 border-cyan-400">
            <td
              colSpan={6}
              className="px-4 py-2 text-xs font-bold text-white uppercase tracking-widest"
            >
              {groupName} · {groupItems.length} item
              {groupItems.length !== 1 ? "s" : ""}
            </td>
          </tr>
          {groupItems.map((data, i) => (
            <tr
              key={`c-${gi}-${i}`}
              onDoubleClick={() => setThresholdRecord(data)}
              title="Double-click to set min stock alert"
              className={`border-b hover:bg-cyan-50 cursor-default select-none ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
            >
              <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
              <td className={`${tdCls} font-medium text-gray-800`}>
                {data.product_name}
              </td>
              <td className={tdCls}>{data.product_code}</td>
              <td className="px-4 py-2.5 border-r whitespace-nowrap">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${stockBadge(data.current_stock, data.min_stock)}`}
                >
                  {formatNumber(data.current_stock)}
                </span>
              </td>
              <td className={tdCls}>{data.unit}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <MinBadge data={data} />
              </td>
            </tr>
          ))}
        </React.Fragment>
      ));
    }
    return currentStock.map((data, i) => (
      <tr
        key={`cs-${i}`}
        onDoubleClick={() => setThresholdRecord(data)}
        title="Double-click to set min stock alert"
        className={`border-b hover:bg-cyan-50 cursor-default select-none ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
      >
        <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
        <td className={`${tdCls} font-medium text-gray-800`}>
          {data.product_name}
        </td>
        <td className={tdCls}>{data.product_code}</td>
        <td className="px-4 py-2.5 border-r whitespace-nowrap">
          <span
            className={`text-xs px-2 py-0.5 rounded ${stockBadge(data.current_stock, data.min_stock)}`}
          >
            {formatNumber(data.current_stock)}
          </span>
        </td>
        <td className={tdCls}>{data.unit}</td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          <MinBadge data={data} />
        </td>
      </tr>
    ));
  };

  /* ── Mobile card view ────────────────────────────────────────────── */
  const renderMobileCards = () => {
    const items =
      viewType === "0"
        ? groupEntries.flatMap(([groupName, groupItems]) =>
            groupItems.map((item) => ({ ...item, _group: groupName })),
          )
        : currentStock;

    if (items.length === 0) return null;

    let lastGroup = null;
    return items.map((data, i) => {
      const groupHeader =
        viewType === "0" && data._group !== lastGroup
          ? ((lastGroup = data._group), data._group)
          : null;

      return (
        <React.Fragment key={`m-${i}`}>
          {groupHeader && (
            <div className="bg-cyan-700 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 mt-3 first:mt-0 rounded-t-lg">
              {groupHeader}
            </div>
          )}
          <div
            onDoubleClick={() => setThresholdRecord(data)}
            className={`flex items-center justify-between px-4 py-3 border-b last:border-b-0 ${
              data.current_stock <= 0
                ? "bg-red-50"
                : data.min_stock != null && data.current_stock <= data.min_stock
                  ? "bg-amber-50"
                  : i % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white"
            }`}
          >
            <div className="flex-1 min-w-0 pr-3">
              <p className="text-sm font-semibold text-gray-800 leading-snug">
                {data.product_name}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {data.product_code}
              </p>
              {data.min_stock != null && (
                <p className="text-xs text-amber-600 mt-0.5">
                  min: {formatNumber(data.min_stock)} {data.unit}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 text-right">
              <span
                className={`text-base font-bold px-2 py-0.5 rounded ${stockBadge(data.current_stock, data.min_stock)}`}
              >
                {formatNumber(data.current_stock)}
              </span>
              <p className="text-xs text-gray-400 mt-0.5">{data.unit}</p>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {thresholdRecord && (
        <StockThresholdModal
          record={thresholdRecord}
          onClose={() => setThresholdRecord(null)}
          onSaved={(product_id, min_stock) => {
            handleThresholdSaved(product_id, min_stock);
            setThresholdRecord(null);
          }}
        />
      )}

      {/* ── Desktop layout: fixed viewport height ───────────────────── */}
      <div
        className="hidden md:flex flex-col"
        style={{ maxHeight: "calc(100dvh - 80px)" }}
      >
        {/* Header */}
        <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
          <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
            Current Stock
          </label>
        </div>

        {/* Body card */}
        <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Controls */}
          <form
            onSubmit={handleSubmit}
            className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4"
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-56">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  View Mode
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-cyan-400 bg-white"
                  value={viewType}
                  onChange={handleViewTypeChange}
                >
                  {VIEW_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {viewType !== "1" && (
                <label className="flex items-center gap-2 cursor-pointer pb-1">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={showEmpty}
                      onChange={() => setShowEmpty((v) => !v)}
                    />
                    <div className="w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-cyan-600 transition-colors" />
                    <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                  <span className="text-sm text-gray-600">
                    Show empty stock
                  </span>
                </label>
              )}
            </div>
            {viewType === "1" && (
              <div className="space-y-3">
                <MaterialLocationSelect onChangeList={setProductLocation} />
                <MaterialListSelect
                  onChangeList={(Arr) => setSelectedMatList(Arr[0])}
                  onOffSwitch={viewType}
                  location_id={productLocation}
                />
              </div>
            )}
            {viewType === "2" && (
              <MaterialGroupSelect onChangeList={setSelectedMatGroup} />
            )}
            <button
              type="submit"
              className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
            >
              Check Stock
            </button>
          </form>

          {/* Table */}
          <div className="flex-1 min-h-0 overflow-auto">
            {!searched ? (
              <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
                Select a view mode and click Check Stock
              </div>
            ) : totalItems === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
                No stock records found
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-gray-700 text-white text-xs uppercase">
                  <tr>
                    <th className={thCls}>No</th>
                    <th className={thCls}>Product Name</th>
                    <th className={thCls}>Product Code</th>
                    <th className={thCls}>Current Stock</th>
                    <th className={thCls}>Unit</th>
                    <th className={thCls}>Min Alert</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            )}
          </div>

          {searched && totalItems > 0 && (
            <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {totalItems} record{totalItems !== 1 ? "s" : ""}
              </span>
              <span className="text-xs text-gray-400 italic">
                Double-click a row to set a min stock alert
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile layout: natural page scroll, no height constraints ── */}
      <div className="md:hidden flex flex-col min-h-screen bg-gray-50 pb-8">
        {/* Header */}
        <div className="bg-cyan-700 px-4 pt-5 pb-4 mb-4">
          <h1 className="text-2xl font-bold text-white text-center uppercase tracking-wide">
            Current Stock
          </h1>
        </div>

        {/* Controls card */}
        <div className="mx-3 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-4">
          <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
            {/* View mode */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                View Mode
              </label>
              <select
                className="w-full border border-gray-300 rounded-xl px-3 py-3 text-base text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                value={viewType}
                onChange={handleViewTypeChange}
              >
                {VIEW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Show empty toggle */}
            {viewType !== "1" && (
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showEmpty}
                    onChange={() => setShowEmpty((v) => !v)}
                  />
                  <div className="w-12 h-7 rounded-full bg-gray-300 peer-checked:bg-cyan-600 transition-colors" />
                  <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                </div>
                <span className="text-sm text-gray-600">Show empty stock</span>
              </label>
            )}

            {/* Selectors */}
            {viewType === "1" && (
              <div className="space-y-3">
                <MaterialLocationSelect onChangeList={setProductLocation} />
                <MaterialListSelect
                  onChangeList={(Arr) => setSelectedMatList(Arr[0])}
                  onOffSwitch={viewType}
                  location_id={productLocation}
                />
              </div>
            )}
            {viewType === "2" && (
              <MaterialGroupSelect onChangeList={setSelectedMatGroup} />
            )}

            <button
              type="submit"
              className="w-full py-4 rounded-xl text-base font-bold text-white bg-cyan-700 active:bg-cyan-900 transition"
            >
              Check Stock
            </button>
          </form>
        </div>

        {/* Results */}
        {!searched ? (
          <div className="flex-1 flex items-center justify-center py-16 text-gray-400 italic text-sm">
            Select a view mode and tap Check Stock
          </div>
        ) : totalItems === 0 ? (
          <div className="flex-1 flex items-center justify-center py-16 text-gray-400 italic text-sm">
            No stock records found
          </div>
        ) : (
          <div className="mx-3 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Result count header */}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {totalItems} item{totalItems !== 1 ? "s" : ""}
              </span>
            </div>
            {renderMobileCards()}
          </div>
        )}
      </div>
    </>
  );
}

export default MaterialCurrentStock;
